/**
 * PHASE 8/9 — picked-object info card (tap-to-identify + SeeIt target info).
 */

import type { PickResult } from "../interact/picking";
import type { CelestialObject } from "../catalog/objectModel";

export interface ObjectCardInfo {
  name: string;
  type: string;
  magnitude?: number;
  meta: string[];
  description?: string;
}

export class InfoCard {
  private root: HTMLElement;
  private nameEl: HTMLElement;
  private metaEl: HTMLElement;
  private descEl: HTMLElement;
  private closeBtn: HTMLElement;

  constructor(container: HTMLElement) {
    this.root = container;
    this.nameEl = container.querySelector<HTMLElement>(".info-name")!;
    this.metaEl = container.querySelector<HTMLElement>(".info-meta")!;
    this.descEl = container.querySelector<HTMLElement>(".info-desc")!;
    this.closeBtn = container.querySelector<HTMLElement>(".info-close")!;
    this.closeBtn.addEventListener("click", () => this.hide());
  }

  show(result: PickResult): void {
    this.showInfo({
      name: result.name,
      type: result.type === "star" ? "star" : result.type === "body" ? "solar-system body" : "deep-sky object",
      magnitude: result.magnitude,
      meta: this.pickMeta(result),
      description: result.details?.description,
    });
  }

  /** Show a catalog/SeeIt object (dynamic bodies, constellations, etc.). */
  showObject(obj: CelestialObject, extraMeta: readonly string[] = []): void {
    const typeLabel = obj.type.toLowerCase().replace("_", " ");
    const meta: string[] = [];
    if (obj.type !== "CONSTELLATION") meta.push(typeLabel);
    if (obj.constellation) meta.push(obj.constellation);
    if (obj.magnitude < 90 && obj.source !== "ephemeris") meta.push(`mag ${obj.magnitude.toFixed(1)}`);
    meta.push(...extraMeta);
    this.showInfo({
      name: obj.name,
      type: typeLabel,
      magnitude: obj.magnitude,
      meta,
      description: obj.description,
    });
  }

  showInfo(info: ObjectCardInfo): void {
    this.nameEl.textContent = info.name;
    this.metaEl.textContent = info.meta.join(" · ");
    this.descEl.textContent = info.description ?? "";
    this.descEl.style.display = info.description ? "" : "none";
    this.root.classList.add("visible");
  }

  hide(): void {
    this.root.classList.remove("visible");
  }

  get visible(): boolean {
    return this.root.classList.contains("visible");
  }

  private pickMeta(result: PickResult): string[] {
    const d = result.details;
    const bits: string[] = [];
    if (d?.constellation) bits.push(d.constellation);
    if (result.magnitude < 90) bits.push(`mag ${result.magnitude.toFixed(1)}`);
    if (d?.designation) bits.push(d.designation);
    if (d?.sizeArcmin) bits.push(`${d.sizeArcmin.toFixed(0)}′`);
    return bits;
  }
}